import { updateDynSessConf } from '@next-app/lib/store';
import { makeFetchCall } from './fetch-data';
import urlMapping from '@next-app/config/urlMapping';
import { REGEX, URLS } from '@next-app/constants/constants';
import { DynamicDTMEvent } from './dtm-event';

interface TrackConversionReq {
  event: string;
  pageName: string;
  skuId?: string;
  orderId?: string;
  trackConversionBaseUrl?: string;
  dynSessConfNumber: string;
  dispatch: Function;
  profileId: string;
  cartId: string;
  productId?: string;
}

const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
});

export const truncateString = (
  str: string | null,
  maxLength: number = 20,
): string => {
  if (!str) return '';
  if (str.length <= maxLength) {
    return str;
  }
  return str.slice(0, maxLength) + '...';
};

export const decodeHtml = (html: string): string => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  let decoded = txt.value;
  txt.innerHTML = decoded;
  return txt.value;
};

export const isBrowser = () => {
  return 'undefined' !== typeof window;
};
export const isValidUrl = (url: string) => {
  const matchPatterns = [
    /^\/?products\/.+/, // Matches any URL that starts with /products/ or products/ followed by additional characters (e.g., /products/item123 or products/item123)
    /\/N\//, // Matches any URL that contains /N/ anywhere in the path (e.g., /category/N/item123)
    /\/p\//, // Matches any URL that contains /N/ anywhere in the path (e.g., /category/N/item123)
    /\/P\//, // Matches any URL that contains /N/ anywhere in the path (e.g., /category/N/item123)
    /\/s\//, // Matches any URL that contains /N/ anywhere in the path (e.g., /category/N/item123)
    /\/S\//, // Matches any URL that contains /N/ anywhere in the path (e.g., /category/N/item123)
    /^\/sale\/.+/, // Matches any URL that starts with /sale/ and is followed by additional characters (e.g., /sale/item123)
    /^\/sale$/, // Matches exactly the URL /sale (without trailing slash)
    /^\/sale\/$/, // Matches exactly the URL /sale/ (with a trailing slash)
    /^\/sale\/$/, // Matches exactly the URL /sale/ (with a trailing slash),
    /^\/search\/.+/, // Matches any URL that starts with /search/ anywhere in the path (e.g., /search/products, /search/videos etc)
    /^\/checkout-page\/.+/, // Matches /checkout-page/
    /^\/shopping-cart\/.+/, // Matches /shopping-cart/
  ];

  const notMatchPatterns = [
    /^\/products\/$/,
    /^\/products\/shop-by-category\/$/,
    /^\/products\/shop-by-age\/$/,
    /^\/products\/classroom-furniture\/$/,
    /^\/products\/learn\/$/,
    /^\/products\/play\/$/,
    /^\/products\/teaching-resources\/$/,
    /^\/products\/featured\/$/,
    /^\/products\/are-you-ready$/,
    /^\/products\/are-you-ready\/$/,
    /^\/products\/flex-space-jr$/,
  ];

  const matches = matchPatterns.some((pattern) => pattern.test(url));

  const notMatches = notMatchPatterns.some((pattern) => pattern.test(url));

  return matches && !notMatches;
};

export const withoutSlash = (path: string) => {
  return path.replace(/^\/+|\/+$/g, '');
};

export const closeHamburgerMenu = () => {
  document.querySelector('.mp-pushed')?.classList.remove('mp-pushed');
};

/**
 * resetHamburgerMenu
 * Closes hamburger menu and waits for 500ms (for animation to complete)
 * before collapsing all open levels
 */
export const resetHamburgerMenu = () => {
  closeHamburgerMenu();
  setTimeout(() => {
    const allActiveNodes = Array.from(
      document.querySelectorAll('nav .active, nav .show'),
    );
    for (let index = 0; index < allActiveNodes.length; index++) {
      allActiveNodes[index]?.classList.remove('active', 'show');
    }
  }, 500);
};

export const resetDesktopMenu = () => {
  const currentlyActiveElems = Array.from(
    document.querySelectorAll('.navigation-list.active'),
  );
  for (let index = 0; index < currentlyActiveElems.length; index++) {
    currentlyActiveElems[index]?.classList.remove('active');
  }
};

export const addTrailingSlash = (link: string = '') => {
  let [path, search] = link.split('?');
  path = path.endsWith('/') ? path : `${path}/`;
  return search ? `${path}?${search}` : path;
};

export const addRouteIsLoadingClass = (routeUrl: string) => {
  if (isBrowser() && routeUrl !== location.pathname) {
    document.body.classList.add('route-is-loading');
  }
};

export const removeRouteIsLoadingClass = () => {
  if (isBrowser()) {
    document.body.classList.remove('route-is-loading');
  }
};

export const printPage = () => {
  DynamicDTMEvent({ event: 'printCart' });
  if (isBrowser()) {
    window.print();
  }
};

function convertTimeToMin(time: any) {
  let [hour, min] = time.split(':');
  // eslint-disable-next-line no-unused-vars
  let [t, ampm] = time.trim().split(' ');
  if (ampm.toLowerCase() === 'am' && parseInt(hour) === 12) {
    hour = 0;
  }

  return parseInt(hour) * 60 + parseInt(min);
}

export function getTimeDiffInHM(t2: string, t1: string) {
  let t1Mins = convertTimeToMin(t1);
  let t2Mins = convertTimeToMin(t2);
  let minDiff = t2Mins - t1Mins;
  return [minDiff / 60, minDiff % 60];
}

export function formatCutOffTime(time: string) {
  if (time.indexOf(':') === -1) {
    time = time.slice(0, 2) + ':' + time.slice(2);
  }
  const [hour, min] = time.split(':');
  // eslint-disable-next-line no-unused-vars
  const [t, ampm] = time.split(' ');
  let formattedTime = parseInt(hour);
  if (ampm && ampm.toLowerCase() == 'pm' && formattedTime != 12) {
    formattedTime += 12;
  }
  return `${formattedTime}:${min}`;
}

export function formatTzToTime(tz: string) {
  var d = new Date();
  return Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

// Replaces "{}" in a string with actual values
export const populateTemplateString = (
  templateString: string,
  data: Record<string, any>,
) => {
  return templateString.replace(/{([^}]+)}/g, function (x) {
    return data[x.replace(/[{}]/g, '')] || '';
  });
};

export const isNotEmptyObject = (value: any = {}) => {
  return value !== null && value !== undefined && Object.keys(value).length > 0;
};

export const isNotEmptyArray = (arr: any[]) => {
  return arr && arr.length > 0;
};

export const isEmptyArray = (arr: any[]) => {
  return arr && arr.length === 0;
};

export const isNotNullOrUndefined = (value: any) => {
  return value !== null && value !== undefined;
};

export const isNullOrUndefined = (value: any) => {
  return value === null && value === undefined;
};

export const isNotNullOrUndefinedorEmpty = (value: any) => {
  return value !== null && value !== undefined && String(value)?.length > 0;
};

export const getFormattedPrice = (
  amount: number | undefined | null,
  currency = '$',
) => {
  if (isNotNullOrUndefined(amount)) {
    const price = numberFormatter.format(Math.abs(amount as number));
    const sign = (amount ?? 0) < 0 ? '-' : '';
    return `${sign}${currency}${price}`;
  } else {
    return '';
  }
};

export const grandTotalPFormatted = (amount: string | number): string => {
  if (isNullOrUndefined(amount)) return '';

  if (typeof amount === 'number') {
    return getFormattedPrice(amount);
  }
  const numericPart = amount?.split('$')[1];
  if (numericPart && !isNaN(Number(numericPart))) {
    return getFormattedPrice(Number(numericPart));
  }
  return amount;
};

export const totalPointsFormatted = (point: string | number): string => {
  if (point == null) return '';

  const number =
    typeof point === 'number' ? point : Number(point.replace(/[^0-9.-]+/g, ''));
  if (isNaN(number)) return '';

  return number.toLocaleString('en-US');
};

export function isCurrentDateInRange(startDate: string, endDate: string) {
  const currentDate = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  return currentDate >= start && currentDate <= end;
}
export const showDynamicModal = () => {
  const modalInstance = new window.bootstrap.Modal('#dynamicModalWrapper');
  modalInstance?.show();
};

export const hideDynamicModal = () => {
  const modalInstance = window?.bootstrap?.Modal?.getInstance(
    '#dynamicModalWrapper',
  );
  modalInstance?.hide();
  hideExtraBackdrops();
};

export const hideExtraBackdrops = () => {
  const backdrops = document.querySelectorAll(
    '.modal-backdrop.fade.show',
  ) as NodeList;

  // Hide all except the last one
  backdrops.forEach((backdrop: any, index) => {
    if (index < backdrops.length - 1) {
      backdrop.style.display = 'none'; // Hide all except the last
    }
  });
  document.body.style.overflow = 'auto';
  document.body.style.removeProperty('padding-right');
};

export const refetch = async ({
  response,
  request,
  dispatch,
}: {
  response: any;
  request: any;
  dispatch: Function;
}) => {
  if (response?.stringSessionConfirmationNumber) {
    const newDynSessConfNumber = response?.stringSessionConfirmationNumber;
    dispatch(updateDynSessConf(newDynSessConfNumber));
    request = {
      ...request,
      body: {
        ...request.body,
        _dynSessConf: newDynSessConfNumber,
      },
    };
    return await makeFetchCall(request);
  }
};

/**
 * Added logic to store user ip in window and re-use it until page is loaded again
 * @param param0
 * @returns
 */
export const TrackConversion = async ({
  event,
  pageName,
  skuId = '',
  orderId = '',
  trackConversionBaseUrl,
  dynSessConfNumber,
  dispatch,
  profileId = '',
  cartId = '',
  productId = '',
}: TrackConversionReq) => {
  let requestIP;
  if (isBrowser() && (window as any)['userip']) {
    requestIP = (window as any)['userip'];
  } else {
    try {
      const requestIpRes = await fetch('/api/get-ip/');
      const reqIp = await requestIpRes.json();
      requestIP = reqIp.ip;
      if (isBrowser()) {
        (window as any)['userip'] = requestIP;
      }
    } catch (e) {
      console.log('Error fetching user ip: ', e);
    }
  }

  const body = {
    event,
    pageName,
    requestIP,
    userAgent: navigator.userAgent,
    _dynSessConf: dynSessConfNumber,
    ...(skuId ? { skuId } : {}),
    ...(orderId ? { orderId } : {}),
    ...(profileId ? { profileId } : {}),
    ...(cartId ? { cartId } : {}),
    ...(productId && { productId }),
  };

  if (trackConversionBaseUrl) {
    let response = await makeFetchCall({
      endpoint: `${trackConversionBaseUrl}${urlMapping?.trackConversionUri}`,
      options: {
        method: 'POST',
      },
      body,
    });
    if (response?.status !== 'ok') {
      response = await refetch({
        response: response,
        request: body,
        dispatch,
      });
    }
    return response;
  }
};

export const isHomePage = (url: string) => {
  return URLS.HOME_PAGE_URLS.includes(url);
};

export const isPlpPage = (url: string) => {
  const contains = (url: string, array: string[]) =>
    array.some((subString) => url.includes(subString));

  return (
    contains(url, URLS.PRODUCT_PAGE_URLS) && !contains(url, URLS.PDP_PAGE_URLS)
  );
};

export const isPdpPage = (url: string) => {
  const contains = (url: string, array: string[]) =>
    array.some((subString) => url.includes(subString));

  return (
    contains(url, URLS.PRODUCT_PAGE_URLS) && contains(url, URLS.PDP_PAGE_URLS)
  );
};

export const isCart = (url?: string) => {
  if (!url) {
    url = getPathname();
  }
  return url.includes(URLS.SHOPPING_CART_PAGE_URL);
};

export const isCheckout = (url?: string) => {
  if (!url) {
    url = getPathname();
  }
  return url.includes(URLS.CHECKOUT_PAGE_URL);
};

export const getPathname = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.pathname;
};

export const closeAllSelectDropdowns = () => {
  const openSelects = document.querySelectorAll('select');
  openSelects.forEach((select) => {
    select.blur();
  });
};

export const formatString = (
  template: string,
  replacements: Record<string, string | number | null>,
) => {
  return template.replace(/{(\d)}/g, (match) => {
    const replacement = replacements[match];
    return replacement !== null && replacement !== undefined
      ? String(replacement)
      : match;
  });
};

export const reversedObject = (originalObject: any) => {
  return Object.entries(originalObject)
    .reverse()
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
};

export const getNumberFormatted = (unformattedNumber: number) => {
  if (isNotNullOrUndefined(unformattedNumber)) {
    const formattedNumber = unformattedNumber.toLocaleString('en-US');
    return formattedNumber;
  } else {
    return 0;
  }
};

export const fetchRecsProductInfo = async (
  recommendedProductsId: string,
  placementId: string,
  title: string,
  dispatch: Function,
  sessionConf: string,
  noOfRecords: number = 25,
) => {
  const productDataCarouselData: any = [];

  if (sessionConf) {
    let payload = {
      endpoint: urlMapping.fetchRecsProductInfo,
      options: {
        method: 'POST',
      },
      body: {
        recommendedProducts: recommendedProductsId,
        maxProdsPerPage: noOfRecords,
        isDisplaySpecialPrice: true,
        _dynSessConf: sessionConf,
      },
    };

    let fallbackProductRecsData: any = await makeFetchCall(payload);
    if (fallbackProductRecsData?.stringSessionConfirmationNumber) {
      fallbackProductRecsData = await refetch({
        response: fallbackProductRecsData,
        request: payload,
        dispatch,
      });
    }

    if (fallbackProductRecsData?.recsProductInfo?.length) {
      productDataCarouselData.push({
        title,
        recsProductInfo: fallbackProductRecsData.recsProductInfo,
        placementId: placementId,
      });
    }
    return productDataCarouselData;
  }
};

/**
 * containsEmptyValues
 * @param obj
 */
export const containsEmptyValues = (obj: any) => {
  return Object.values(obj).every((value) => {
    return !isNotNullOrUndefinedorEmpty(value);
  });
};

export const containsSomeEmptyValues = (obj: any) => {
  return Object.values(obj).some((value) => {
    return !isNotNullOrUndefinedorEmpty(value);
  });
};

export const getIsFurnitureTab = () => {
  let isFurniture = false;
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    const furnitureTabRegex = REGEX.FURNITURE_TAB_URL;
    if (furnitureTabRegex.test(currentPath)) {
      isFurniture = true;
    }
  }
  return isFurniture;
};

export const handlePaste = (e: any) => {
  const pastedData = e.clipboardData.getData('text');
  if (!/^\d*$/.test(pastedData)) {
    e.preventDefault();
  }
};

/**
 * Fetches a new session confirmation number to renew the session.
 */
export const getSessionNumber = async (isShoppingCartPage?: boolean) => {
  if (isShoppingCartPage && typeof document !== 'undefined') {
    document.cookie = 'retainSessionOrder=true; path=/';
  }
  const sessionConfRes: any = await makeFetchCall({
    endpoint: urlMapping.sessionConfirmationNumberUrl,
  });
  return sessionConfRes;
};

export const extractText = (node: any): string => {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node?.props?.children) return extractText(node.props.children);
  return '';
};

export const getFurnitureTabUrl = (url: string) => {
  const furnitureTabRegex = REGEX.FURNITURE_TAB_URL;
  if (furnitureTabRegex.test(url)) {
    return url;
  } else {
    return `${URLS.FURNITURE_TAB_URL_PREFIX}${url}`;
  }
};

export const getCountryStateList = async () => {
  const countryStateListResponse: any = await makeFetchCall({
    endpoint: urlMapping.countryStateList,
  });
  return countryStateListResponse;
};

export const validateAllFormFields = (
  fields: Record<string, any>,
  validateField: Function,
  setFormErrors: Function,
) => {
  let errorMessages: Record<string, string> = {};
  Object.entries(fields).forEach(([name, value]) => {
    errorMessages[name] = validateField(name, value);
  });
  setFormErrors((prev: any) => ({ ...prev, ...errorMessages }));
  return containsEmptyValues(errorMessages);
};
